import React from 'react';
import { Box, Avatar, Typography, CircularProgress } from '@mui/material';
import { useResolvedUploaderProfile } from '../../hooks/useResolvedUploaderProfile';
import {
   getUploaderDisplayName,
   hasUploaderMediaIds,
   type UploaderProfileLike,
} from './MediaUploaderAttribution';
import { PhotographerBadge } from './PhotographerBadge';

interface PhotoLike {
   userId?: string;
   uploadedBy?: string;
   uploaderProfile?: UploaderProfileLike | Record<string, unknown> | null;
}

/**
 * Timeline card uploader row: loads profile from Firestore / public API using `userId` / `uploadedBy` only.
 */
export function AlbumTimelineUploaderRow({
   photo,
   isMobile,
   onOpenProfile,
   noTopMargin,
}: {
   photo: PhotoLike;
   isMobile?: boolean;
   onOpenProfile?: (uid: string) => void;
   noTopMargin?: boolean;
}) {
   const uidResolved = String((photo.userId || photo.uploadedBy) || '').trim();
   const { profile, loading } = useResolvedUploaderProfile(
      uidResolved || undefined,
      photo.uploaderProfile ?? null,
   );
   if (!hasUploaderMediaIds(photo.userId, photo.uploadedBy)) return null;
   const uid = uidResolved;
   const name = getUploaderDisplayName(profile);
   const isPhotographer = (profile.accountType || []).includes('photographer');

   const mt = noTopMargin ? 0 : 1;
   if (loading && !name) {
      return (
         <Box sx={{ display: 'flex', alignItems: 'center', mt, minHeight: 24 }}>
            <CircularProgress size={isMobile ? 14 : 16} />
         </Box>
      );
   }
   if (!name) return null;

   return (
      <Box
         onMouseDown={(e) => e.stopPropagation()}
         onClick={(e) => {
            e.stopPropagation();
            if (uid && onOpenProfile) onOpenProfile(uid);
         }}
         sx={{
            display: 'flex',
            alignItems: 'center',
            mt,
            cursor: uid && onOpenProfile ? 'pointer' : 'default',
         }}
      >
         <Box sx={{ position: 'relative', flexShrink: 0, mr: isMobile ? 0.5 : 1 }}>
            <Avatar
               sx={{
                  width: isMobile ? 16 : 20,
                  height: isMobile ? 16 : 20,
               }}
               src={
                  profile.profilePicture ||
                  profile.photoURL ||
                  profile.profilePhoto ||
                  profile.avatar
               }
            >
               {name.charAt(0) || '?'}
            </Avatar>
            {isPhotographer && <PhotographerBadge variant='avatarOverlay' size='sm' />}
         </Box>
         <Typography
            variant='caption'
            color='text.secondary'
            noWrap
            sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}
         >
            {name}
         </Typography>
      </Box>
   );
}
